// English translations
export const en = {
  // Navigation
  nav: {
    about: 'About Us',
    packages: 'Packages',
    technologies: 'Technologies',
    services: 'Services',
    process: 'Process',
    contact: 'Contact',
    cta: 'Get in Touch'
  },
  
  // Hero Section
  hero: {
    title1: 'AI Consulting',
    title2: 'of the New Era',
    subtitle: 'Business automation and digital transformation',
    description: 'We create intelligent business systems using AI. From strategy to full implementation.',
    ctaPrimary: 'Choose Package',
    ctaSecondary: 'Consultation'
  },
  
  // About Section
  about: {
    title: 'Your Partner in Digital Transformation',
    paragraph1: 'The world is accelerating. Companies implementing AI today become tomorrow\'s leaders.',
    paragraph2: 'From strategy to full implementation — we create intelligent business systems with measurable results.',
    whyChoose: 'Why Choose Us',
    benefits: [
      {
        title: 'Full Cycle — From Strategy to Implementation',
        description: 'We don\'t just consult — we create working solutions. We analyze, design, implement and support until results are achieved.'
      },
      {
        title: 'Numbers That Prove Results',
        description: 'We measure everything: time, efficiency, profit. KPI, ROI and BI analytics show how AI makes your business faster, more accurate and more profitable.'
      },
      {
        title: 'AI — A Tool, Not the Future',
        description: 'We implement technologies that already bring profit to business today — from task automation to data-driven analytics.'
      },
      {
        title: 'International Experience and Flexibility',
        description: 'Based in Estonia, we work with clients worldwide. We adapt solutions to different markets and scales.'
      },
      {
        title: 'Partnership, Not Contracting',
        description: 'We\'re not just consultants — we integrate with your team and deliver the project to tangible impact.'
      }
    ]
  },
  
  // Packages Section
  packages: {
    title: 'Solutions for Digital Growth',
    subtitle: 'From strategy to automation and AI implementation',
    popular: 'Popular',
    learnMore: 'Learn More',
    result: 'Result:',
    items: [
      {
        name: 'INSIGHT',
        icon: '🔴',
        tagline: 'Analysis and growth strategy',
        price: '€1,500 - €2,500',
        description: 'For small and medium businesses, startups',
        features: [
          'Complete business and process audit',
          'Competitor and digital maturity analysis',
          'SWOT analysis and growth points map',
          'AI and automation recommendations',
          '3-month mini-strategy',
          'Strategic session (90 min)'
        ],
        result: 'Understanding where business loses money and a clear growth plan'
      },
      {
        name: 'GROWTH ENGINE',
        icon: '🔵',
        tagline: 'Systematic growth and automation',
        price: '€4,500 - €9,000',
        description: 'Transition to systematic growth and digitalization',
        features: [
          'Everything from INSIGHT package',
          '6-month strategy',
          'CRM / ERP systems implementation',
          'AI chatbots and auto-funnels',
          'End-to-end analytics and BI dashboards',
          'HR consulting and KPI implementation',
          '3 months support'
        ],
        result: 'Stable growth, transparent management, process automation'
      },
      {
        name: 'AI TRANSFORM',
        icon: '🟣',
        tagline: 'Digital business transformation',
        price: '€12,000 - €30,000',
        description: 'For medium and large businesses',
        features: [
          'Everything from GROWTH ENGINE package',
          'Annual Digital & AI roadmap',
          'Comprehensive process automation',
          'AI solutions implementation',
          'IT infrastructure modernization',
          'Staff training',
          'Individual project curator'
        ],
        result: 'Complete AI transformation: scalable data-driven ecosystem'
      },
      {
        name: 'PROJECT+',
        icon: '⚫',
        tagline: 'Custom solutions',
        price: 'from €5,000',
        description: 'Turnkey custom projects',
        features: [
          'AI bots and assistants',
          'CRM/ERP automation',
          'SaaS or marketplace creation',
          'BI analytics and forecasting',
          'Investment preparation',
          'AI integration into processes'
        ],
        result: 'Ready solution adapted to your goals and scale'
      }
    ]
  },
  
  // Technologies Section
  technologies: {
    title: 'Technologies and Tools',
    subtitle: 'Advanced automation solutions',
    items: [
      {
        title: 'Artificial Intelligence',
        description: 'Machine Learning, NLP, computer vision, predictive analytics',
        keywords: ['ChatGPT', 'Claude', 'Gemini', 'Custom AI']
      },
      {
        title: 'Process Automation',
        description: 'RPA, workflow automation, API integrations, business processes',
        keywords: ['Make', 'Zapier', 'n8n', 'Custom Scripts']
      },
      {
        title: 'CRM & ERP Systems',
        description: 'Implementation, configuration and optimization of management systems',
        keywords: ['Bitrix24', 'Salesforce', 'SAP', '1C']
      },
      {
        title: 'BI & Analytics',
        description: 'Business Intelligence, dashboards, data visualization',
        keywords: ['Power BI', 'Tableau', 'Looker', 'Custom']
      }
    ]
  },
  
  // Services Section
  services: {
    title: 'Our Services',
    subtitle: 'Comprehensive solutions for business growth',
    items: [
      {
        title: 'Business Consulting',
        description: 'Strategic planning, process optimization, profit increase'
      },
      {
        title: 'Digital Transformation',
        description: 'Business digitalization, modern technology implementation, scaling'
      },
      {
        title: 'Financial Consulting',
        description: 'Financial analysis, cost optimization, planning and risk management'
      },
      {
        title: 'Legal Support',
        description: 'Corporate law, contracts, GDPR, international legislation'
      },
      {
        title: 'HR Consulting',
        description: 'Personnel management, KPI, team motivation, talent development'
      },
      {
        title: 'Automation',
        description: 'Business process automation, integrations, efficiency improvement'
      },
      {
        title: 'AI & ML Implementation',
        description: 'Artificial intelligence for business, analytics, forecasting, chatbots'
      }
    ]
  },
  
  // Why Us Section
  whyUs: {
    title: 'Results That Speak for Themselves',
    subtitle: 'Our efficiency metrics',
    items: [
      {
        prefix: 'up to',
        number: '92%',
        title: 'Efficiency Growth',
        description: 'Average productivity increase after implementing our solutions'
      },
      {
        prefix: 'up to',
        number: '6 mo',
        title: 'ROI',
        description: 'Average return on investment timeframe for automation and AI'
      },
      {
        number: '24/7',
        title: 'Support',
        description: 'Technical support and consultations at all stages'
      },
      {
        prefix: '> ',
        number: '50',
        title: 'Projects',
        description: 'Successfully implemented AI and automation projects'
      }
    ]
  },
  
  // Process Section
  process: {
    title: 'Work Process',
    subtitle: 'From analysis to results',
    items: [
      {
        number: '01',
        title: 'Analysis and Audit',
        description: 'Deep analysis of business, processes and current systems'
      },
      {
        number: '02',
        title: 'Strategy',
        description: 'Roadmap development and digital transformation plan'
      },
      {
        number: '03',
        title: 'Implementation',
        description: 'Phased implementation of AI, automation and management systems'
      },
      {
        number: '04',
        title: 'Training',
        description: 'Staff training to work with new technologies'
      },
      {
        number: '05',
        title: 'Optimization',
        description: 'Continuous improvement and solution scaling'
      }
    ]
  },
  
  // Contact Section
  contact: {
    title: 'Get in Touch',
    subtitle: 'Ready to start your business transformation?',
    selectedPackage: 'Selected package:',
    goalQuestion: 'What is the main goal of your request?',
    goals: [
      'Process automation',
      'AI implementation',
      'Efficiency improvement',
      'Consulting'
    ],
    multipleChoice: 'You can select multiple options',
    form: {
      name: 'Name *',
      email: 'Email *',
      company: 'Company name *',
      companyDescription: 'Brief description of what your company does *',
      message: 'Additional message (optional)',
      terms: 'I have read and agree to the',
      termsLink: 'terms of service',
      and: 'and',
      privacyLink: 'privacy policy',
      termsNote: '*. By ordering services through any channel (email, phone, etc.), you automatically agree to our terms of service.',
      submit: 'Send Message',
      submitting: 'Sending...',
      success: 'Thank you! We will contact you shortly.',
      error: 'An error occurred. Please try again later.'
    }
  },
  
  // Footer
  footer: {
    description: 'Next-generation consulting. AI, automation and digital business transformation.',
    company: 'Polyus Plus OÜ',
    navigation: 'Navigation',
    legal: 'Legal Information',
    links: {
      about: 'About Us',
      packages: 'Packages',
      services: 'Services',
      contact: 'Contact Us',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      cookie: 'Cookie Policy',
      cookieSettings: 'Cookie Settings'
    },
    rights: 'All rights reserved.',
    keywords: 'AI consulting | Business automation | Digital transformation'
  },
  
  // Cookie Banner
  cookie: {
    title: '🍪 Cookie Notice',
    description: 'We use cookies to improve your browsing experience and analytics.',
    accept: 'Accept',
    decline: 'Decline',
    settings: 'Settings',
    moreDetails: 'More details →',
    settingsModal: {
      title: 'Cookie Settings',
      subtitle: 'Choose which cookies to allow',
      alwaysEnabled: 'Always enabled',
      saveSettings: 'Save Settings',
      acceptAll: 'Accept All',
      categories: {
        essential: {
          name: 'Essential cookies',
          description: 'These cookies are necessary for the website to function and cannot be disabled.'
        },
        analytics: {
          name: 'Analytics cookies',
          description: 'Google Analytics 4, Hotjar - help understand how visitors use the site.'
        },
        marketing: {
          name: 'Marketing cookies',
          description: 'Meta Pixel, TikTok Pixel - used for targeted advertising.'
        },
        functional: {
          name: 'Functional cookies',
          description: 'Improve site functionality (save language, theme, etc.).'
        }
      }
    }
  },
  
  // Meta and SEO
  meta: {
    title: 'Polyus Plus | AI Consulting and Business Automation in Tallinn, Estonia',
    description: 'Professional AI consulting, business process automation, and digital transformation. Artificial intelligence implementation, efficiency increase up to 92%, transparent ROI. Consulting in Tallinn, working worldwide.',
    keywords: 'AI consulting, business automation, digital transformation, AI implementation, business processes, consulting Estonia, Tallinn, artificial intelligence, KPI, ROI, business efficiency, Google Analytics 4, Hotjar, Facebook Pixel',
    ogTitle: 'Polyus Plus | AI Consulting and Business Automation',
    ogDescription: 'Professional AI consulting, business process automation and digital transformation. Efficiency increase up to 92%, transparent ROI.',
    siteName: 'Polyus Plus',
    footer: {
      company: 'About Company',
      about: 'About Us',
      packages: 'Packages',
      services: 'Services',
      contact: 'Contact Us',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      cookie: 'Cookie Policy',
      cookieSettings: 'Cookie Settings'
    },
    rights: 'All rights reserved.'
  }
};
